import React from 'react'
import { StyleSheet, useWindowDimensions, View } from 'react-native'

import { HardwareWalletSigningRequest } from '@ambire-common/interfaces/signAccountOp'
import Button from '@common/components/Button'
import FooterGlassView from '@common/components/FooterGlassView'
import Text from '@common/components/Text'
import { useTranslation } from '@common/config/localization'
import SigningRequestDetails from '@common/modules/hardware-wallets/components/SigningRequestDetails'
import spacings from '@common/styles/spacings'
import flexbox from '@common/styles/utils/flexbox'
import text from '@common/styles/utils/text'
// Lazy-loaded: `@keystonehq/animated-qr` pulls in `@zxing/library` (~1.2 MB),
// only needed when a QR-based hardware wallet is actually signing. Splitting it
// out of the eager bundle keeps it off the boot path for everyone else.
const AnimatedQRCode = React.lazy(() =>
  import('@keystonehq/animated-qr').then((m) => ({ default: m.AnimatedQRCode }))
)

type Props = {
  onContinue: () => void
  onReject: () => void
  urType: any
  urCborHex: any
  transactionProgress?: {
    current: number
    total: number
  } | null
  signingRequest?: HardwareWalletSigningRequest | null
}

const ANIMATION_INTERVAL = 300

// Same white rounded plate the Receive popup puts its QR on — the code needs a
// light background to scan reliably in the dark theme anyway.
const styles = StyleSheet.create({
  qrBox: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 16,
    alignItems: 'center',
    justifyContent: 'center'
  }
})

const QrSignRequestScreen = ({
  onContinue,
  onReject,
  urType,
  urCborHex,
  transactionProgress = null,
  signingRequest = null
}: Props) => {
  const { t } = useTranslation()
  const { width: windowWidth } = useWindowDimensions()
  // Sized like the Receive popup rather than the old fixed 300px, which
  // overflowed a narrow phone and dwarfed the dialog on desktop. The sheet is
  // capped at 480 and padded, so cap the code accordingly and shrink with the
  // viewport; a signing payload is denser than an address, hence the 180 floor.
  const qrSize = Math.max(180, Math.min(transactionProgress ? 220 : 240, windowWidth - 112))

  return (
    <View style={[flexbox.alignCenter, { width: '100%' }]}>
      <Text style={text.center}>{t('Scan this QR code with your QR-based device to sign.')}</Text>
      <View style={[flexbox.alignCenter, spacings.mtSm, { width: '100%' }]}>
        <View style={styles.qrBox}>
          <React.Suspense fallback={<View style={{ width: qrSize, height: qrSize }} />}>
            <AnimatedQRCode
              options={{ size: qrSize, interval: ANIMATION_INTERVAL }}
              type={urType}
              cbor={urCborHex}
            />
          </React.Suspense>
        </View>
        {transactionProgress ? (
          <Text fontSize={14} weight="medium" style={spacings.mtSm}>
            {transactionProgress.current} / {transactionProgress.total}{' '}
            {transactionProgress.current <= 1 ? t('transaction signed') : t('transactions signed')}
          </Text>
        ) : null}
        {!!signingRequest && (
          <SigningRequestDetails
            signingRequest={signingRequest}
            style={[spacings.mt, { width: '100%', maxWidth: 420 }]}
          />
        )}
        <FooterGlassView size="sm" absolute={false} style={spacings.ptSm}>
          <Button
            size="small"
            hasBottomSpacing={false}
            type="secondary"
            text={t('Back')}
            onPress={onReject}
            style={{ width: 98, ...spacings.mrLg }}
          />
          <Button
            size="small"
            hasBottomSpacing={false}
            text={t('Get signature')}
            onPress={onContinue}
          />
        </FooterGlassView>
      </View>
    </View>
  )
}

export default React.memo(QrSignRequestScreen)
